import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { auth } from '../firebase';
import type { UserProfile } from '../types';
import {
    refreshGlobalDividendCalendarForDev,
    type DevGlobalDividendCalendarRefreshDiagnostics,
} from '../services/devGlobalDividendCalendarRefreshService';

type ThemeMode = 'light' | 'dark';

type Props = {
    user: UserProfile | null;
    theme: ThemeMode;
    isDevOverride?: boolean;
    endpointBase?: string;
};

const isViteDev = () => {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') return true;
    if (typeof window === 'undefined') return false;
    return ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);
};

export const canShowGlobalDividendCalendarRefresh = (
    user: UserProfile | null,
    isDev = isViteDev(),
) => Boolean(user && isDev);

const diagnosticFields: Array<keyof DevGlobalDividendCalendarRefreshDiagnostics> = [
    'status',
    'providerRequestCount',
    'rowsFetched',
    'normalizedEvents',
    'docsWritten',
    'errors',
];

const formatDiagnosticValue = (value: unknown) => {
    if (Array.isArray(value)) return value.length ? JSON.stringify(value) : '[]';
    if (value === undefined || value === null) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const DevGlobalDividendCalendarRefresh = ({ user, theme, isDevOverride, endpointBase }: Props) => {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [diagnostics, setDiagnostics] = useState<DevGlobalDividendCalendarRefreshDiagnostics | null>(null);
    const [error, setError] = useState<string | null>(null);

    if (!canShowGlobalDividendCalendarRefresh(user, isDevOverride ?? isViteDev())) {
        return null;
    }

    const handleRefreshClick = async () => {
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) {
            setError('You must be signed in to refresh the global dividend calendar.');
            return;
        }

        setIsRefreshing(true);
        setError(null);
        try {
            const result = await refreshGlobalDividendCalendarForDev(firebaseUser, endpointBase);
            setDiagnostics(result);
        } catch (refreshError) {
            setDiagnostics(null);
            setError(refreshError instanceof Error ? refreshError.message : 'Global dividend calendar refresh failed.');
        } finally {
            setIsRefreshing(false);
        }
    };

    const panelClasses = theme === 'light'
        ? 'border-amber-200 bg-amber-50 text-amber-950'
        : 'border-amber-400/30 bg-amber-500/10 text-amber-50';
    const mutedClasses = theme === 'light' ? 'text-amber-800' : 'text-amber-100/75';
    const diagnosticClasses = theme === 'light'
        ? 'border-amber-200 bg-white/70 text-slate-800'
        : 'border-amber-300/20 bg-black/20 text-amber-50';

    return (
        <div className={`rounded-2xl border p-4 ${panelClasses}`} data-testid="global-dividend-calendar-dev-refresh">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h3 className="text-sm font-semibold uppercase tracking-[0.2em]">Local dev tools</h3>
                    <p className={`mt-1 text-sm ${mutedClasses}`}>
                        Local development helper only. Production refreshes are backend-only through the scheduled function.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleRefreshClick}
                    disabled={isRefreshing}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {isRefreshing && <Loader2 size={16} className="animate-spin" />}
                    Refresh global dividend calendar
                </button>
            </div>

            {diagnostics && (
                <dl className={`mt-4 grid gap-2 rounded-xl border p-3 text-sm sm:grid-cols-2 ${diagnosticClasses}`}>
                    {diagnosticFields.map((field) => (
                        <div key={field} className="flex justify-between gap-3">
                            <dt className="font-medium">{field}</dt>
                            <dd className="text-right font-mono text-xs">{formatDiagnosticValue(diagnostics[field])}</dd>
                        </div>
                    ))}
                </dl>
            )}

            {error && (
                <p className={`mt-3 rounded-lg border border-rose-300 bg-rose-500/10 px-3 py-2 text-sm ${theme === 'light' ? 'text-rose-700' : 'text-rose-100'}`}>
                    {error}
                </p>
            )}
        </div>
    );
};

export default DevGlobalDividendCalendarRefresh;
